import { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => { document.title = "SIFT - Privacy Policy"; }, []);

  const section = { marginBottom: 28 };
  const h2 = { fontSize: 18, fontWeight: 700, color: "#1a1a1a", marginBottom: 8 };
  const p = { fontSize: 14, lineHeight: 1.7, color: "#555", marginBottom: 8 };

  return (
    <div style={{ fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif", maxWidth: 720, margin: "0 auto", padding: "48px 24px 80px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');`}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2a6b45" }} />
        <span style={{ fontWeight: 800, fontSize: 14, color: "#2a6b45" }}>SIFT</span>
        <span style={{ color: "#e0e0e0", margin: "0 4px" }}>/</span>
        <span style={{ fontSize: 13, color: "#999", fontWeight: 500 }}>Privacy Policy</span>
      </div>

      <h1 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", marginBottom: 6 }}>Privacy Policy</h1>
      <p style={{ fontSize: 13, color: "#999", marginBottom: 36 }}>Last updated: 27 March 2026</p>

      <div style={section}>
        <h2 style={h2}>1. Introduction</h2>
        <p style={p}>SIFT ("we", "our", "us") is a Shopify application that helps DTC brands optimise their product feeds using consumer search intelligence. This policy explains how we collect, use, and protect your data when you install and use SIFT.</p>
      </div>

      <div style={section}>
        <h2 style={h2}>2. Data We Collect</h2>
        <p style={p}><strong>Store data:</strong> When you install SIFT, we access your Shopify store name, product listings (titles, descriptions, tags, metafields, images), and store configuration through the Shopify Admin API. We only access what's needed to analyse and optimise your feed.</p>
        <p style={p}><strong>Google data:</strong> If you connect Google Search Console or Google Analytics, we access search query data, click/impression metrics, and on-site search terms. This data is used to enrich product signals.</p>
        <p style={p}><strong>Social signals:</strong> We collect publicly available data from platforms like Reddit to understand how consumers describe product categories relevant to your catalogue.</p>
        <p style={p}><strong>Account data:</strong> Your Shopify store URL, plan selection, and billing status.</p>
      </div>

      <div style={section}>
        <h2 style={h2}>3. How We Use Your Data</h2>
        <p style={p}>We use your data exclusively to:</p>
        <ul style={{ ...p, paddingLeft: 20 }}>
          <li>Analyse your product feed for completeness and GMC compliance</li>
          <li>Generate AI-optimised product copy using consumer search signals</li>
          <li>Identify gaps between what consumers search and what your feed contains</li>
          <li>Push approved changes back to your Shopify store</li>
          <li>Display analytics and performance insights within the app</li>
        </ul>
      </div>

      <div style={section}>
        <h2 style={h2}>4. AI Processing</h2>
        <p style={p}>We use Anthropic Claude to generate optimised product copy. Your product data and search signals are sent to Claude's API for processing. Generated content is reviewed by you before any changes are pushed to your store. We do not use your data to train AI models.</p>
      </div>

      <div style={section}>
        <h2 style={h2}>5. Data Storage & Security</h2>
        <p style={p}>Your data is stored in encrypted databases. API tokens (Shopify, Google) are stored securely and never exposed to the frontend. We use HTTPS for all data transmission. Access to production systems is restricted to authorised personnel.</p>
      </div>

      <div style={section}>
        <h2 style={h2}>6. Data Retention</h2>
        <p style={p}>We retain your data for as long as SIFT is installed on your store. When you uninstall the app, we delete your store data, products, and API tokens within 30 days. Aggregated, anonymised analytics may be retained for product improvement.</p>
      </div>

      <div style={section}>
        <h2 style={h2}>7. Third-Party Services</h2>
        <p style={p}>We share data with the following third parties, solely for providing the SIFT service:</p>
        <ul style={{ ...p, paddingLeft: 20 }}>
          <li><strong>Shopify</strong> - Store data access and billing via Shopify APIs</li>
          <li><strong>Google</strong> - Search Console and Analytics data via Google APIs</li>
          <li><strong>Anthropic</strong> - AI copy generation via Claude API</li>
        </ul>
        <p style={p}>We do not sell your data to any third party.</p>
      </div>

      <div style={section}>
        <h2 style={h2}>8. Your Rights (GDPR)</h2>
        <p style={p}>If you're in the EU/UK, you have the right to access, rectify, erase, or port your personal data. You can also object to processing. To exercise these rights, contact us at <strong>privacy@sift.ai</strong>.</p>
        <p style={p}>We process data under legitimate interest (providing the service you've installed) and, where applicable, your consent (connecting Google services).</p>
      </div>

      <div style={section}>
        <h2 style={h2}>9. Data Erasure</h2>
        <p style={p}>You can request complete deletion of your data at any time by emailing <strong>privacy@sift.ai</strong> or by uninstalling the app from your Shopify admin. We process deletion requests within 30 days.</p>
      </div>

      <div style={section}>
        <h2 style={h2}>10. Changes to This Policy</h2>
        <p style={p}>We may update this policy as the product evolves. Material changes will be communicated through the app. Continued use of SIFT after changes constitutes acceptance of the updated policy.</p>
      </div>

      <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: 20, marginTop: 40 }}>
        <p style={{ fontSize: 12, color: "#999" }}>Contact: privacy@sift.ai</p>
      </div>
    </div>
  );
}
